import _ from 'lodash';
import moment from 'moment-timezone';
import ReservationUtils from '@wix/wixrest-utils/dist/openrest-helpers/Reservation';
import PhoneUtils from '@wix/wixrest-utils/dist/PhoneUtils';
import TimeUtils from '@wix/wixrest-utils/dist/Time';
import TranslatedContent from '@wix/wixrest-utils/dist/TranslatedContent';
import { I18N } from '@wix/restaurants-i18n-js';
import { PARAMS_KEYS } from '../constants';
import TOKENS from '../../tokens';
import { WixInstance, PropertiesUtils as OPU } from '@wix/wixrest-utils';
import Properties from '@wix/wixrest-properties';


const MAX_DELAY_MINUTES = 60 * 24 * 30 * 6;
const PHONE_DELIMITER = '##';
const HEADER = 'reservations_app_';

export function printTime(time) {
    return time.format('LT');
}

export function getDateErrors (restaurant, errors, content) {
    const phone = PhoneUtils.displayPhone(_.get(restaurant, 'contact.phone', ''), _.get(restaurant, 'address.countryCode'));
    let minutes = TimeUtils.humanizeMinutesDuration(_.get(restaurant, 'reservations.futureDelayMins.min', 0));
    const maxTime = TimeUtils.humanizeMinutesDuration(_.get(restaurant, 'reservations.futureDelayMins.max', MAX_DELAY_MINUTES));
    let dateError = null;

    if (errors && (errors.date || errors.time)) {
        switch (errors.date) {
            case 'isPast':
                dateError = content.get('error_date_past');
                break;
            case 'isEarly':
                dateError = insertPhone(content.get('error_date_early', {params: {time: minutes, phone: PHONE_DELIMITER}}), phone);
                break;
            case 'isLate':
                dateError = insertPhone(content.get('error_date_late', {params: {time: maxTime, phone: PHONE_DELIMITER}}), phone);
                break;
            case 'closed':
                dateError = insertPhone(content.get('error_date_closed', {params: {phone: PHONE_DELIMITER}}), phone);
                break;
            case 'bad_time':
                dateError = content.get('error_date_bad', {params: {hours: getReservationHours(restaurant)}});
                break;
            default:
                dateError = content.get('error_date_default', {params: {comment: errors.date}});
        }
    }

    return dateError;
}

function getReservationHours (restaurant) {
    const availability = _.get(restaurant, 'reservations.availability', {});
    const openTimes = _.get(restaurant, 'openTimes', {});
    return ReservationUtils.getReservationTimePrint(availability, openTimes);
}

function insertPhone (str, phone) {
    let arr = str.split(PHONE_DELIMITER);
    return (
        <div>
            {arr[0] ? <span>{arr[0]}</span> : null}
            {phone ? <span style={{whiteSpace: 'nowrap'}}>{phone}</span> : null}
            {arr[1] ? <span>{arr[1]}</span> : null}
        </div>
    );
}

export function getMaxDelay(reservations) {
    return _.get(reservations, 'futureDelayMins.max', MAX_DELAY_MINUTES);
}

function getContent (localeData) {
    return (new TranslatedContent({header: HEADER, tokens: TOKENS, i18n: new I18N(localeData)})).instance('');
}

function getPartyOptions ({min, max} = {min: 1, max: 5}, localeData) {
    const content = getContent(localeData);
    let partyOptions = [];

    for (let i = min; i <= max; i++) {
        partyOptions.push({
            name: i === 1 ? content.get('party_size_option_single') : content.get('party_size_option_plural', {params: {count: i}}),
            value: i
        });
    }

    return partyOptions;
}

function getTimeOptions(ReservationUtils, restaurant) {
    const _moment = new moment();
    const restaurantLocale = _.get(restaurant, 'locale', 'en_US');
    _moment.locale(restaurantLocale)
        .tz(restaurant.timezone);

    const options = _.map(ReservationUtils.getReservationHourOption(restaurant, null),
        option => {
            const time = _moment.clone().minutes(option.minutes).hour(option.hour);
            return {
                value: option,
                name: printTime(time),
                type: 'time'
            };
        });
    return options;
}

export function getFormOptions(ReservationUtils, restaurant, localeData) {
    const reservations = _.get(restaurant, 'reservations', {});
    const availability = _.get(reservations, 'availability', {});
    const partySize = _.get(reservations, 'partySize', {});
    const openTimes = _.get(restaurant, 'openTimes', {});
    const partyOptions = getPartyOptions(partySize, localeData);
    const timezone = _.get(restaurant, 'timezone', '');
    const excludeDates = ReservationUtils.getNonAvailableDaysForPeriod(availability, openTimes,
        _.get(reservations, 'futureDelayMins.min', 0), getMaxDelay(reservations), 'minutes', moment().tz(timezone), timezone);
    const timeOptions = getTimeOptions(ReservationUtils, restaurant);

    return {partyOptions, excludeDates, timeOptions};
}

function getSelectedTitle (selectedText, localeData) {
    const content = getContent(localeData);
    let options = [
        content.get('title_preset1'),
        content.get('title_preset2'),
        content.get('title_preset3'),
        content.get('title_preset4'),
        content.get('title_preset5')
    ];
    return options[selectedText];
}

function getTitle (styles, compId, localeData) {
    const selectedText = styles.numbers[PARAMS_KEYS.SELECTED_TITLE_KEY];
    const isCustom = selectedText === -1;
    let title = '';

    if (isCustom) {
        title = styles.data[`${PARAMS_KEYS.CUSTOM_TITLE_DATA_KEY}.${compId}`] &&
            styles.data[`${PARAMS_KEYS.CUSTOM_TITLE_DATA_KEY}.${compId}`].value || '';
    } else {
        title = getSelectedTitle(selectedText, localeData);
    }

    return title;
}

function getSelectedButtonText (selectedButtonText, localeData) {
    const content = getContent(localeData);
    let options = [
        content.get('button_preset1'),
        content.get('button_preset2'),
        content.get('button_preset3'),
        content.get('button_preset4'),
        content.get('button_preset5')
    ];
    return options[selectedButtonText];
}

function getButtonText (styles, compId, localeData) {
    const selectedButtonText = styles.numbers[PARAMS_KEYS.SELECTED_BUTTON_KEY];
    const isCustom = selectedButtonText === -1;
    let buttonText = '';

    if (isCustom) {
        buttonText = styles.data[`${PARAMS_KEYS.CUSTOM_BUTTON_DATA_KEY}.${compId}`] &&
            styles.data[`${PARAMS_KEYS.CUSTOM_BUTTON_DATA_KEY}.${compId}`].value || '';
    } else {
        buttonText = getSelectedButtonText(selectedButtonText, localeData);
    }

    return buttonText;
}

export function getSettings(styles, compId, localeData) {
    const showTitle = styles.booleans[PARAMS_KEYS.SHOW_TITLE_KEY];
    const showDescription = styles.booleans[PARAMS_KEYS.SHOW_DESCRIPTION_KEY];
    const layout = styles.numbers[PARAMS_KEYS.LAYOUT_KEY];
    const customDescription = styles.data[`${PARAMS_KEYS.CUSTOM_DESCRIPTION_DATA_KEY}.${compId}`] &&
        styles.data[`${PARAMS_KEYS.CUSTOM_DESCRIPTION_DATA_KEY}.${compId}`].value || '';
    const customTitle = getTitle(styles, compId, localeData);
    const customButton = getButtonText(styles, compId, localeData);
    const isCustomDesign = styles.numbers[PARAMS_KEYS.SELECTED_PRESET] === -1;
    return {showTitle, showDescription, layout, customTitle, customDescription, customButton, isCustomDesign};
}

export function isEnabled(restaurant) {
    return {isEnabled: ReservationUtils.isEnabled(restaurant).isEnabled};
}

export function isPremium(restaurant){
    return !!_.find(restaurant.products, (product) => product.param === 'premium');
}

export function createReservationRequestObj (reservationDetails, restaurant) {
    const restaurantReservationsInfo = restaurant.reservations;
    let time = moment.tz(reservationDetails.date, restaurant.timezone);
    time.hours(reservationDetails.time.hour);
    time.minutes(reservationDetails.time.minutes);
    const partySize = reservationDetails.party;
    const comment = reservationDetails.requests || '';
    const contact = createContactObject(reservationDetails, restaurant.address.countryCode);
    const msid = getMetasiteId();

    let reservationObj = {
        externalIds: {
            msid
        },
        time: time.valueOf(),
        partySize,
        contact,
        comment,
        restaurantId: restaurant.id,
        locale: restaurant.locale,
        developer: 'com.wix.restaurants',
        source: '',
        heldUntil: _.isUndefined(restaurantReservationsInfo.heldForMins) ? null : time.clone().add(restaurantReservationsInfo.heldForMins, 'minutes').valueOf(),
        platform: 'web',
        timeGuarantee: 'about',
        status: restaurantReservationsInfo.pendingApproval ? 'new' : 'accepted',
        properties: {}
    };

    let customFields = reservationDetails.customFields;
    if (customFields) {
        // Getting rid of nulls
        customFields = customFields.filter(cf => !!cf);
        OPU.set(reservationObj, Properties.Reservations.CUSTOMFIELDS, customFields, 'com.wix.restaurants');
    }
    return reservationObj;
}

function getMetasiteId() {
    try {
        return _.get(WixInstance.getParsedInstance(), 'metaSiteId', '');
    } catch (err) {
        console.error('getMetasiteId', err);
        return '';
    }
}

function createContactObject (reservationDetails, countryCode) {
    const { firstName, lastName, phone, email } = reservationDetails;
    const displayPhone = PhoneUtils.validatePhone(phone, countryCode);
    return { firstName, lastName, phone: displayPhone, email };
}

export function getErrorType ({type = ''} = {type: ''}) {
    return type.substr(type.lastIndexOf('/') + 1);
}
